import clsx from 'clsx';
import { useRef } from 'react';
import { Transition } from '@headlessui/react';
import { useIntersection } from 'react-use';

// Utils
import { useProfileHasAccessPage } from '@/utils/hooks/acl/useProfileHasAccessPage';
import useDisplayFirstStatsPanel from '@/utils/hooks/firstStats/useDisplayFirstStatsPanel';

// Components
import SelectedProfiles from '@/components/composite/SelectedProfiles';
import { PopoverList } from '@/components/layouts/LayoutPlatform/components/PageHeaderRecap/PopoverList';

export type PageHeaderChildrenProps = {
    isSticky?: boolean;
};

export type DropdownLinksType = { href: string; label: React.ReactNode }[];

type PageHeaderRecapProps = {
    title: React.ReactNode;
    children?: (args: PageHeaderChildrenProps) => React.ReactNode;
    dropdownLinks?: DropdownLinksType;
};

export function PageHeaderRecap({ title, children, dropdownLinks }: PageHeaderRecapProps) {
    const headerRef = useRef<HTMLDivElement>();
    const headerIntersection = useIntersection(headerRef, {
        threshold: 0,
        root: document.querySelector('.main-content-scroll'),
        rootMargin: '100px 0px 0px 0px'
    });

    const {
        activeGroupNoAccess,
        activeMultiSelectionProfilesNoAccess,
        activeSocialProfileNoAccess
    } = useProfileHasAccessPage();

    const { hideFirstStatsEmptyState } = useDisplayFirstStatsPanel();

    const displayChildren =
        children &&
        !activeGroupNoAccess &&
        !activeMultiSelectionProfilesNoAccess &&
        !activeSocialProfileNoAccess &&
        hideFirstStatsEmptyState;

    const isSticky = Boolean(headerRef.current && !headerIntersection?.isIntersecting);

    return (
        <>
            <div
                className="sticky top-0 z-50 hidden sm:-mt-8 sm:block"
                data-testid="sticky-page-header-recap">
                <Transition
                    show={isSticky}
                    enter="transition-all ease-in-out duration-500 transform"
                    enterFrom="-translate-y-full"
                    enterTo="translate-y-8"
                    leave="transition ease-in-out duration-300 transform"
                    leaveFrom="translate-y-8"
                    leaveTo="-translate-y-full"
                    className="absolute -left-5 -top-5 z-20 w-[calc(100%+40px)] sm:-left-8 sm:-top-8 sm:w-[calc(100%+64px)]">
                    <div
                        className={clsx(
                            'flex w-full items-center justify-between rounded-t-2xl border-b px-4 py-2.5',
                            'border-icoGray-300 dark:border-icoDarkMode-beton',
                            'bg-white dark:bg-icoDarkMode-deepBlack'
                        )}>
                        <div className="flex flex-1 items-center gap-5">
                            <div className="flex items-center gap-2">
                                <PopoverList links={dropdownLinks} isSticky={true} />
                                <p className="font-GBold">{title}</p>
                            </div>
                            <div className="min-w-44 flex-1">
                                <SelectedProfiles itemsSize="md" />
                            </div>
                        </div>
                        {displayChildren ? <div>{children({ isSticky: true })}</div> : null}
                    </div>
                </Transition>
            </div>
            <div ref={headerRef} className="relative z-20 mb-8 sm:my-8">
                <div
                    className={clsx('flex flex-1 flex-col gap-4', {
                        '@[500px]/wrapper-platform-content:flex-row @[500px]/wrapper-platform-content:items-center @[500px]/wrapper-platform-content:gap-8':
                            !children,
                        '@[1144px]/wrapper-platform-content:flex-row @[1144px]/wrapper-platform-content:items-center @[1144px]/wrapper-platform-content:gap-8':
                            children
                    })}>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="z-20 flex items-center space-x-3">
                            <PopoverList links={dropdownLinks} />
                            <h2 className="font-GBold text-2xl @[500px]/wrapper-platform-content:text-3.5xl">
                                {title}
                            </h2>
                        </div>
                        {displayChildren ? (
                            <div className="@[500px]/wrapper-platform-content:hidden">
                                {children({ isSticky: false })}
                            </div>
                        ) : null}
                    </div>
                    <div className="flex flex-1 flex-row">
                        <div className="flex-1">
                            <SelectedProfiles />
                        </div>
                        {displayChildren ? (
                            <div className="hidden items-center @[500px]/wrapper-platform-content:flex">
                                {children({ isSticky: false })}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
}
